import { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Home, LogIn } from 'lucide-react';
import { useAuthStore } from '@stores/authStore';
import { BrandLogo } from '@components/brand/BrandLogo';
import { ThemeToggle } from '@components/ThemeToggle';

interface ErrorPageProps {
  /** Small label above the title, e.g. "404" or "500". */
  code?: string;
  /** Main heading. */
  title?: string;
  /** Supporting message under the heading. */
  message?: string;
  /** Custom icon; defaults to a warning triangle. */
  icon?: ReactNode;
  /**
   * Rendered inside the app shell (sidebar and header already on screen), so
   * it fills its container and brings no branding of its own.
   */
  embedded?: boolean;
}

/**
 * Full-page, user-friendly error screen. Used for unmatched routes (404) and
 * anywhere we want to fail gracefully instead of dumping the user on a blank
 * page or bouncing them through a silent redirect.
 *
 * Visually mirrors ErrorBoundary's fallback so crashes and not-found states
 * feel consistent.
 */
export function ErrorPage({
  code = '404',
  title = 'Page not found',
  message = "We couldn't find the page you were looking for. It may have been moved, or the link might be broken.",
  icon,
  embedded = false,
}: ErrorPageProps) {
  const navigate = useNavigate();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // React Router keeps the entry's position in `history.state.idx`. At 0 this
  // is the first page of the tab (a pasted or bookmarked URL), and "back"
  // would leave the app for a blank page.
  const goBack = () => {
    if ((window.history.state?.idx ?? 0) > 0) navigate(-1);
    else navigate(isAuthenticated ? '/dashboard' : '/login');
  };

  return (
    <div
      className={`relative flex items-center justify-center bg-[var(--co-canvas)] px-4 ${
        embedded ? 'h-full min-h-[420px]' : 'min-h-screen'
      }`}
    >
      {!embedded && (
        <div className="absolute inset-x-0 top-0 flex items-center justify-between px-6 py-5">
          <BrandLogo height={24} />
          <ThemeToggle />
        </div>
      )}
      <div className="max-w-md w-full text-center">
        <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center dark:bg-red-900/30">
          {icon ?? <AlertTriangle className="w-8 h-8 text-red-600" />}
        </div>

        {code && (
          <p className="text-sm font-semibold uppercase tracking-wide text-primary-600 mb-2">
            {code}
          </p>
        )}

        <h1 className="text-2xl font-bold text-gray-900 mb-2 dark:text-gray-100">
          {title}
        </h1>
        <p className="text-gray-600 mb-6 dark:text-gray-400">{message}</p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <button
            onClick={goBack}
            className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Go Back
          </button>
          <button
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/login')}
            className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {isAuthenticated ? <Home className="w-4 h-4 mr-2" /> : <LogIn className="w-4 h-4 mr-2" />}
            {isAuthenticated ? 'Go to Dashboard' : 'Go to sign in'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ErrorPage;
